import onClick from "@/js/libs/onClick";
import loadjs from "loadjs";
import axios from "axios";

const SquarePaymentGatewayForm = () => {
  const APP = window.APP || {};
  let form;
  let card;
  let cardButton;
  let messageContainer;

  /**
   * Dictionary of Square card form error messages
   * Note that some of the below are not included deliberately so a generic message will be displayed
   * Some of these are likely not to display of the Square form widget does is job correctly
   *
   * https://developer.squareup.com/docs/devtools/sandbox/payments#card-present-success-state-values
   * https://developer.squareup.com/reference/square/payments-api/create-payment#error-descriptions
   */
  const errorMessages = {
    ADDRESS_VERIFICATION_FAILURE: "The zipcode you entered is incorrect",
    CARDHOLDER_INSUFFICIENT_PERMISSIONS:
      "Your card issuer declined the transaction due to insufficient funds",
    CARD_EXPIRED:
      "Your card issuer declined the transaction because your card has expired",
    CARD_NOT_SUPPORTED:
      "Your card issuer declined the transaction as this card type is not supported",
    CARD_TOKEN_EXPIRED:
      "Your payment request took too long to process; please try again",
    CARD_TOKEN_USED: "Payment has already been taken; please refresh your page",
    CVV_FAILURE: "The CVV you entered is incorrect",
    EXPIRATION_FAILURE:
      "The card expiry date is either invalid or indicates that your card has expired",
    GENERIC_DECLINE: "Your card issuer declined the transaction",
    INSUFFICIENT_FUNDS:
      "Your card issuer declined the transaction due to insufficient funds",
    INVALID_CARD: "Your card details are invalid",
    INVALID_CARD_DATA: "Your card details are invalid",
    INVALID_EXPIRATION: "The expiry date you entered is incorrect",
    INVALID_POSTAL_CODE: "The zipcode you entered is invalid",
    PAN_FAILURE: "The card number you entered is invalid",
    CARD_DECLINED_VERIFICATION_REQUIRED:
      "Your card issuer declined your card as further verification is required; please contact us",
    TEMPORARY_ERROR: "A temporary error occurred; please try again",
  };

  /**
   * Updates the payment component state
   * @param ready
   * @param message
   * @param isError
   */
  const setStatus = (ready, message, isError) => {
    if(isError) {
      messageContainer.classList.remove("hidden");
      messageContainer.classList.add("text-errorColor");
    } else {
      messageContainer.classList.remove("text-errorColor");
      messageContainer.classList.add("hidden");
    }
    if (ready) {
      cardButton.removeAttribute("disabled");
      cardButton.innerHTML = "Make Payment";
      cardButton.style.opacity = 1;
      cardButton.dataset.state = "ready";
    } else {
      cardButton.setAttribute("disabled", "disabled");
      cardButton.setAttribute("disabled", "true");
      cardButton.innerHTML = "Please wait...";
      cardButton.style.opacity = 0.5;
      cardButton.dataset.state = "loading";
    }
    if (isError) {
      cardButton.dataset.state = "disabled";
    }
    messageContainer.textContent = message ? message : "";
  };

  /**
   * Outputs a friendly error message
   * @param err
   * @returns {string}
   */
  const formatErrorMessage = (err) => {
    for (let code in errorMessages) {
      if (err.indexOf(code) !== -1) {
        return errorMessages[code];
      }
    }

    return "Sorry, an error occurred: " + err;
  };

  /**
   * Handles a successful payment request
   * Could include declines and other gateway errors
   * @param res
   */
  const success = (res) => {
    window.location.href = form.querySelector('[name="redirect"]').value;
  };

  /**
   * Handles a failed payment request
   * For ajax and runtime errors
   * @param err
   */
  const failure = (err) => {
    setStatus(true, "Sorry, an error occurred", true);
  };

  /**
   * Initializes the Square card widget
   * @param payments
   * @returns {Promise<void>}
   */
  async function initializeCard(payments) {
    card = await payments.card();
    await card.attach("[data-square-card-widget]");
  }

  /**
   * Retrieves a token that can be used to create a payment
   * @returns {Promise<boolean|*>}
   */
  async function getToken() {
    const tokenResult = await card.tokenize();
    if (tokenResult.status === "OK") {
      return tokenResult.token;
    }
    // let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
    // if (tokenResult.errors) {
    //   errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
    // }
    // console.warn(errorMessage);
    return false;
  }

  /**
   * Handles submission of the Square card form widget
   * Once a token has been received from Square, that is then submitted to
   * Craft Commerce where the Square Gateway handles the payment using that token
   * @param e
   * @returns {Promise<void>}
   */
  const submitHandler = async (e) => {
    e.preventDefault();

    setStatus(false);

    const token = await getToken();

    if (!token) {
      return setStatus(true, "Error with card", true);
    }

    const body = {
      "paymentForm.square": {
        sourceId: token,
      },
    };

    body[APP.config.csrfName] = APP.config.csrfValue;

    axios
      .post("/actions/commerce/payments/pay", body)
      .then(success)
      .catch(failure);
  };

  /**
   * Prepares the payment component when the page loads
   * @returns {Promise<void>}
   */
  const init = async () => {
    form = document.querySelector("[data-square-card-payment-form]");
    cardButton = document.querySelector("[data-square-card-submit]");
    messageContainer = document.querySelector("[data-square-card-message]");

    if (!form) {
      return;
    }

    loadjs(APP.square.script, {
      success: async function() {
        const payments = window.Square.payments(
          APP.square.applicationId,
          APP.square.locationId,
        );
        await initializeCard(payments);

        onClick(cardButton, submitHandler);

        document
          .querySelector("[data-square-card-loading-container]")
          .classList.add("hidden");
        document
          .querySelector("[data-square-card-ready-container]")
          .classList.remove("hidden");

        setStatus(true);
      },
      error: function() {
        return setStatus(
          false,
          "An error occurred when loading the payment form.",
          true,
        );
      }
    });
  };

  if (document.readyState !== "loading") {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init);
  }
};

export default SquarePaymentGatewayForm;
